import React from 'react';
import Card from '../Card';
import EmptyStateCard from '../EmptyStateCard';
import DataTable, { TableColumn } from '../DataTable';
import ProgressBar from '../ProgressBar';
import { PlanVsActualMetrics, UserUtilization, getVarianceColor } from '../../utils/reportingUtils';

interface PlanVsActualTabProps {
  planVsActual: PlanVsActualMetrics | null;
  userUtilization: UserUtilization[];
  selectedWeekStart: string;
  weekEndDate: string;
  onWeekChange: (newWeekStart: string) => void;
}

interface ProjectVarianceRow {
  projectId: string;
  projectName: string;
  plannedHours: number;
  actualHours: number;
  variance: number;
  variancePercent: number;
}

const PlanVsActualTab: React.FC<PlanVsActualTabProps> = ({
  planVsActual,
  userUtilization,
  selectedWeekStart,
  weekEndDate,
  onWeekChange
}) => {
  const projectVarianceData: ProjectVarianceRow[] = planVsActual?.projectBreakdown.map(project => ({
    projectId: project.projectId,
    projectName: project.projectName,
    plannedHours: project.plannedHours,
    actualHours: project.actualHours,
    variance: project.variance,
    variancePercent: project.variancePercent
  })) || [];

  const varianceColumns: TableColumn<ProjectVarianceRow>[] = [
    {
      key: 'projectName',
      label: 'Project',
      render: (row) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{row.projectName}</span>
      )
    },
    {
      key: 'plannedHours',
      label: 'Planned',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.plannedHours.toFixed(1)}h</span>
      )
    },
    {
      key: 'actualHours',
      label: 'Actual',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.actualHours.toFixed(1)}h</span>
      )
    },
    {
      key: 'variance',
      label: 'Variance',
      render: (row) => (
        <span className="font-bold" style={{ color: getVarianceColor(row.variancePercent) }}>
          {row.variance >= 0 ? '+' : ''}{row.variance.toFixed(1)}h
        </span>
      )
    },
    {
      key: 'variancePercent',
      label: 'Variance %',
      render: (row) => (
        <span 
          className="px-2 py-1 text-xs font-semibold rounded-full" 
          style={{ backgroundColor: getVarianceColor(row.variancePercent), color: 'white' }}
        >
          {row.variancePercent >= 0 ? '+' : ''}{row.variancePercent.toFixed(0)}%
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const progressPercent = row.plannedHours > 0 
          ? Math.min((row.actualHours / row.plannedHours) * 100, 100) 
          : 0;
        return (
          <div style={{ width: '120px' }}>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: getVarianceColor(row.variancePercent),
                }}
              />
            </div>
          </div>
        );
      }
    }
  ];

  const getUtilizationColor = (rate: number): string => {
    if (rate < 50) return '#ef4444';
    if (rate < 90) return '#22c55e';
    if (rate <= 110) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Planning vs Actuals Analysis</h2>

      {/* Week Selector */}
      <Card className="mb-6">
        <Card.Body>
          <div className="flex gap-4 items-center flex-wrap">
            <div className="mb-0 flex-1 min-w-[250px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Week (Monday)
              </label>
              <input
                type="date"
                className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                value={selectedWeekStart}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  if (selectedDate.getDay() === 1) {
                    onWeekChange(e.target.value);
                  } else {
                    alert('Please select a Monday');
                  }
                }}
              />
            </div>
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-md">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Week: {new Date(selectedWeekStart).toLocaleDateString()} - {new Date(weekEndDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {!planVsActual ? (
        <EmptyStateCard
          icon="📋"
          title="No Weekly Plan Found"
          message="There are no weekly plans for the selected week. Create a plan in the Weekly Planner to see variance analysis."
        />
      ) : (
        <>
          {/* Summary Card */}
          <div 
            className="mb-6 border-l-4 rounded-lg" 
            style={{ borderLeftColor: getVarianceColor(planVsActual.variancePercent) }}
          >
            <Card>
              <Card.Body>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Week Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Planned Hours</div>
                    <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                      {planVsActual.totalPlannedHours.toFixed(1)}h
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Actual Hours</div>
                    <div className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">
                      {planVsActual.totalActualHours.toFixed(1)}h
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Variance</div>
                    <div 
                      className="text-2xl font-bold" 
                      style={{ color: getVarianceColor(planVsActual.variancePercent) }}
                    >
                      {planVsActual.variance >= 0 ? '+' : ''}{planVsActual.variance.toFixed(1)}h
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Variance %</div>
                    <div 
                      className="text-2xl font-bold" 
                      style={{ color: getVarianceColor(planVsActual.variancePercent) }}
                    >
                      {planVsActual.variancePercent >= 0 ? '+' : ''}{planVsActual.variancePercent.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <ProgressBar 
                    percentage={planVsActual.totalPlannedHours > 0 
                      ? Math.min((planVsActual.totalActualHours / planVsActual.totalPlannedHours) * 100, 100) 
                      : 0
                    }
                    color="primary"
                    height="lg"
                  />
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Variance by Project */}
          <Card className="mb-6">
            <Card.Body>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Variance by Project</h3>
              <DataTable
                columns={varianceColumns}
                data={projectVarianceData}
                emptyMessage="No project data for this week"
                getRowKey={(row) => row.projectId}
              />
            </Card.Body>
          </Card>

          {/* User Utilization */}
          <Card>
            <Card.Body>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">User Utilization</h3>
              {userUtilization.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">No time entries for this week</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {userUtilization.map(user => (
                    <div 
                      key={user.userId} 
                      className="border-l-4 rounded-lg p-4 bg-white dark:bg-gray-800 mb-4" 
                      style={{ borderLeftColor: getUtilizationColor(user.utilizationRate) }}
                    >
                      <h4 className="mt-0 text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {user.userName}
                      </h4>
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Hours Logged</div>
                          <div className="font-bold text-xl text-gray-900 dark:text-gray-100">
                            {user.totalHoursLogged.toFixed(1)}h
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Utilization</div>
                          <div className="font-bold text-xl text-gray-900 dark:text-gray-100">
                            {user.utilizationRate.toFixed(0)}%
                          </div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <div className="progress-bar">
                          <div 
                            className="progress-bar-fill"
                            style={{
                              width: `${Math.min(user.utilizationRate, 100)}%`,
                              backgroundColor: getUtilizationColor(user.utilizationRate),
                            }}
                          />
                        </div>
                      </div>
                      {user.projects.length > 0 && (
                        <div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Project Breakdown:</div>
                          {user.projects.map(project => (
                            <div 
                              key={project.projectId} 
                              className="flex justify-between py-1 border-b border-gray-200 dark:border-gray-700"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {project.projectName}
                              </span>
                              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                {project.hours.toFixed(1)}h
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default PlanVsActualTab;
